import React, { lazy, Suspense, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getHelpAndGuides,
  AddHelpAndGuide,
  EditHelpAndGuide,
  DeleteHelpAndGuide,
} from "../../API/FaqAPI/FaqAPI";
import Loader from "../../components/Loader/Loader";
import { FiPlus, FiHelpCircle, FiMessageSquare, FiX } from "react-icons/fi";

const Wrapper = lazy(() => import("../../components/wrapper/Wrapper"));
const Question = lazy(() => import("./components/Question"));

const validationSchema = Yup.object({
  question: Yup.string().required("Question is required"),
  answer: Yup.string().required("Answer is required"),
});

const FAQs = () => {
  const [showAnswer, setShowAnswer] = useState(null);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [helpAndGuide, setHelpAndGuide] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState({
    buttonLoading: false,
    index: null,
  });

  const fetchHelpAndGuide = async () => {
    try {
      setIsLoading(true);
      const helpAndGuides = await getHelpAndGuides();
      setHelpAndGuide(helpAndGuides);
    } catch (error) {
      console.error("Error fetching help and guide:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHelpAndGuide();
  }, []);

  const handleAnswer = (questionId) => {
    setShowAnswer(showAnswer === questionId ? null : questionId);
  };

  const handleDeleteQuestion = async (index, item) => {
    try {
      setDeleteLoading({ buttonLoading: true, index });
      await DeleteHelpAndGuide(item);
      setHelpAndGuide((prev) => prev.filter((guide) => guide._id !== item._id));
      setDeleteLoading({ buttonLoading: false, index: null });
    } catch (error) {
      setDeleteLoading({ buttonLoading: false, index: null });
      console.error("Error deleting help and guide:", error);
    } finally {
      setDeleteLoading({ buttonLoading: false, index: null });
      setButtonLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      question: "",
      answer: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setButtonLoading(true);
        if (editingItem) {
          const updatedHelpGuide = await EditHelpAndGuide({
            ...editingItem,
            ...values,
          });
          setHelpAndGuide((prev) =>
            prev.map((item) =>
              item._id === updatedHelpGuide._id ? updatedHelpGuide : item
            )
          );
        } else {
          const newHelpGuide = await AddHelpAndGuide(values);
          setHelpAndGuide((prev) => [...prev, newHelpGuide]);
        }
        resetForm();
        setIsModelOpen(false);
        setEditingItem(null);
      } catch (error) {
        console.error("Error saving help and guide:", error);
      } finally {
        setButtonLoading(false);
      }
    },
  });

  const handleEditQuestion = (item) => {
    setEditingItem(item);
    formik.setValues({
      question: item.question,
      answer: item.answer,
    });
    setIsModelOpen(true);
  };

  return (
    <Suspense fallback={<div className="m-10">Loading...</div>}>
      <Wrapper>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <FiHelpCircle className="w-8 h-8 text-[--primary]" />
                Frequently Asked Questions
              </h1>
              <p className="text-gray-600 mt-2">
                Manage and organize your FAQ content
              </p>
            </div>
            <button
              onClick={() => setIsModelOpen(true)}
              className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors flex items-center gap-2 font-medium shadow-sm"
            >
              <FiPlus className="w-5 h-5" />
              Add Question
            </button>
          </div>
        </div>

        {/* FAQs Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader />
            </div>
          ) : helpAndGuide.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {helpAndGuide.map((item, index) => (
                <Question
                  key={item._id}
                  index={index}
                  data={item}
                  showAnswer={showAnswer === item._id}
                  handleAnswer={handleAnswer}
                  handleEditQuestion={() => handleEditQuestion(item)}
                  handleDeleteQuestion={handleDeleteQuestion}
                  deleteLoading={deleteLoading}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FiMessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQs Found</h3>
              <p className="text-gray-500 mb-6">Get started by adding your first frequently asked question.</p>
              <button
                onClick={() => setIsModelOpen(true)}
                className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors flex items-center gap-2 font-medium shadow-sm mx-auto"
              >
                <FiPlus className="w-5 h-5" />
                Add First Question
              </button>
            </div>
          )}
        </div>
      </Wrapper>

      {/* Custom FAQ Modal */}
      {isModelOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingItem ? "Edit Question" : "Add New Question"}
                </h2>
                <button
                  onClick={() => {
                    setIsModelOpen(false);
                    setEditingItem(null);
                    formik.resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* Question Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question
                  </label>
                  <input
                    type="text"
                    name="question"
                    value={formik.values.question}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter your question here..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                  />
                  {formik.touched.question && formik.errors.question ? (
                    <div className="text-red-500 text-sm mt-1">{formik.errors.question}</div>
                  ) : null}
                </div>

                {/* Answer Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Answer
                  </label>
                  <textarea
                    name="answer"
                    value={formik.values.answer}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter the answer here..."
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors resize-none"
                  />
                  {formik.touched.answer && formik.errors.answer ? (
                    <div className="text-red-500 text-sm mt-1">{formik.errors.answer}</div>
                  ) : null}
                </div>
              </form>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsModelOpen(false);
                  setEditingItem(null);
                  formik.resetForm();
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={formik.handleSubmit}
                disabled={buttonLoading}
                className="px-6 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {buttonLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {editingItem ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  editingItem ? "Update Question" : "Add Question"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Suspense>
  );
};

export default FAQs;
